import { useMemo } from "react";
import styles from "./OpeningHours.module.css";
import Holiday from "../../models/Holiday";
import { DEFAULT_OPENING_HOURS, OpenHours } from "../../models/OpenHours";
import FadeInComponent from "../ui/FadeInComponent";
import {
  getFirstDateOfWeek,
  getFormattedDateBasedOnStringDateAndDuration,
  getLastDateOfWeek,
  parseDate
} from "../../lib/date-time/DateUtils";
import {
  monthDateYearFormatter,
  monthDayFormatter,
} from "../../lib/date-time/DateFormatter";
import { weekDays } from "../../models/WeekDays";

interface OpeningHoursProps {
  holidayData: Holiday[];
}

function OpeningHours({ holidayData }: OpeningHoursProps) {
  const today = useMemo(() => new Date(), []);

  const firstDateOfWeek = useMemo(() => getFirstDateOfWeek(today), [today]);
  const lastDateOfWeek = useMemo(() => getLastDateOfWeek(today), [today]);

  const weeklyHolidays = useMemo(() => {
    return holidayData.filter((holiday) => {
      const startDate = parseDate(holiday.observingStartDate);
      const endDate = parseDate(holiday.observingEndDate);

      return endDate >= firstDateOfWeek && startDate <= lastDateOfWeek;
    });
  }, [holidayData, firstDateOfWeek, lastDateOfWeek]);

  const openingHours = useMemo(() => {
    return getOpeningHoursForWeek(weeklyHolidays, firstDateOfWeek, lastDateOfWeek);
  }, [weeklyHolidays, firstDateOfWeek, lastDateOfWeek]);

  const weeklyScheduleHeading = useMemo(() => {
    const sameYear = firstDateOfWeek.getFullYear() === lastDateOfWeek.getFullYear();

    return `${sameYear
      ? monthDayFormatter.format(firstDateOfWeek)
      : monthDateYearFormatter.format(firstDateOfWeek)
    } - ${monthDateYearFormatter.format(lastDateOfWeek)}`;
  }, [firstDateOfWeek, lastDateOfWeek]);

  return (
    <section id="openingHours">
      <FadeInComponent
        className={`${styles["my-opening-hours"]} container-fluid d-flex flex-column align-items-center justify-content-center gap-5 pt-4 pb-5 px-3`}
      >
        <div className="text-center my-heading">
          <h2 className="m-0">Opening Hours</h2>
          <p className="m-0">
            Come visit us during these hours, so we can serve you some unphogettable memories
          </p>
        </div>

        <div className="w-100 row justify-content-center align-items-center">
          <div
            className="col-8 col-md-4 d-flex flex-column justify-content-center align-items-center gap-3"
            style={{ minWidth: "300px" }}
          >
            <h5 className="m-0">{weeklyScheduleHeading}</h5>

            <div className={`${styles["custom-overlay"]} w-100 h-100 d-flex flex-column justify-content-center gap-3 rounded-4 p-4`}>
              {openingHours.map((hour) => (
                <div
                  key={hour.dayOfWeek}
                  data-testid="opening-hour-row"
                  className={`row rounded-2 ${
                    weekDays[today.getDay()] === hour.dayOfWeek
                      ? styles["active-day"]
                      : ""
                  }`}
                >
                  <div className="col-6 text-end">{hour.dayOfWeek}</div>
                  <div className="col-6 text-start">
                    {hour.openHour === ""
                      ? "CLOSED"
                      : `${hour.openHour} - ${hour.closingHour}`}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="w-100 row justify-content-center align-items-center">
          <div className="col-8 col-md-6 d-flex flex-column justify-content-center align-items-center">
            <h5>Special Schedule</h5>

            {weeklyHolidays.length ? (
              <>
                <p>We will be closing on the following days:</p>
                <ul className={`${styles["custom-overlay"]} rounded-4 p-4`}>
                  {weeklyHolidays.map((holiday) => (
                    <li key={holiday.id}>
                      <span className="fw-bold">{holiday.name}</span>:{" "}
                      {getFormattedDateBasedOnStringDateAndDuration(
                        holiday.observingStartDate,
                        holiday.observingEndDate
                      )}
                    </li>
                  ))}
                </ul>
              </>
            ) : (
              <p>We're not expecting any days off this week.</p>
            )}
          </div>
        </div>
      </FadeInComponent>
    </section>
  );
}

function getOpeningHoursForWeek(
  holidays: Holiday[],
  firstDateOfWeek: Date,
  lastDateOfWeek: Date
): OpenHours[] {
  const openingHours = DEFAULT_OPENING_HOURS.map((hour) => ({ ...hour }));

  for (const holiday of holidays) {
    const startDate = parseDate(holiday.observingStartDate);
    const endDate = parseDate(holiday.observingEndDate);

    const effectiveStart = startDate < firstDateOfWeek ? firstDateOfWeek : startDate;
    const effectiveEnd = endDate > lastDateOfWeek ? lastDateOfWeek : endDate;

    for (
      let curDate = new Date(effectiveStart);
      curDate <= effectiveEnd;
      curDate.setDate(curDate.getDate() + 1)
    ) {
      openingHours[curDate.getDay()] = {
        ...openingHours[curDate.getDay()],
        openHour: "",
      };
    }
  }

  return openingHours;
}

export default OpeningHours;